/**
 * Spec 014 — edits of a task's description, title and due date.
 *
 * Added text reads in the «Сделано» green and removed text is muted and struck
 * (not red: in the brand red is the «Препятствие» status only, Spec 020 D2);
 * every line also keeps its «＋»/«−» sign, so the meaning never rests on colour
 * alone. The changed span of a long line is bold.
 */

import { useQuery } from "@tanstack/react-query";
import { PencilLine } from "lucide-react";
import { Link } from "react-router-dom";

import type { ChatMessage } from "@/api/chat";
import { Tasks } from "@/api/resources";
import type { DiffLineOut, RevisionField } from "@/api/types";
import { cn } from "@/lib/cn";
import { formatDateTime, parseDate } from "@/lib/format";

const TONE = { "+": "text-p-done", "-": "text-p-text-muted line-through" } as const;

export function DiffRow({ line }: { line: DiffLineOut }) {
  const sign = line.sign === "+" ? "＋" : "−";
  return (
    <p className={cn("whitespace-pre-wrap break-words text-body", TONE[line.sign])}>
      <span aria-label={line.sign === "+" ? "добавлено" : "убрано"}>{sign} </span>
      {line.changed ? (
        <>
          {line.head}
          <strong>{line.changed}</strong>
          {line.tail}
        </>
      ) : (
        line.text
      )}
    </p>
  );
}

/** One line of a notice's plain-text body; `**span**` is the changed part. */
function NoticeLine({ line }: { line: string }) {
  const tone = line.startsWith("＋") ? TONE["+"] : line.startsWith("−") ? TONE["-"] : "text-p-text";
  const parts = line.split(/\*\*(.+?)\*\*/);
  return (
    <p className={cn("whitespace-pre-wrap break-words text-meta", tone)}>
      {parts.map((part, i) => (i % 2 ? <strong key={i}>{part}</strong> : part))}
    </p>
  );
}

export function isSystemNotice(message: Pick<ChatMessage, "kind">): boolean {
  return Boolean(message.kind && message.kind !== "message");
}

/**
 * A notice by «Система» in a discussion: who changed what, and the difference.
 * Spec 021 — a quiet line in the feed, as on the canvas, not a card: an icon,
 * the change and its time, the difference, «История правок · Ответить».
 * `inContext`: shown inside its own task, so no link back to it.
 */
export function SystemNotice({ message, onReply, inContext = false }: { message: ChatMessage; onReply: () => void; inContext?: boolean }) {
  const [head = "", ...lines] = message.body_md.split("\n");
  // The kind names the leading field; JSON key order is not preserved by the server.
  const field = (message.kind?.replace(/_changed$/, "") || "description") as RevisionField;
  const at = parseDate(message.created_at);
  return (
    <article id={`message-${message.id}`} aria-label="Системное сообщение" className="flex gap-2.5 px-1">
      <PencilLine className="mt-0.5 h-4 w-4 shrink-0 text-p-text-muted" aria-hidden="true" />
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="text-meta text-p-text-muted">
          <span className="font-semibold text-p-text">{head.replace(/^⚙️\s*/, "")}</span>
          {at && <> · <time dateTime={message.created_at}>{formatDateTime(at)}</time></>}
        </p>
        {!inContext && message.parent_type !== "chat" && (
          <Link to={message.context_url} className="block text-meta text-p-link">
            {/* A case's title already reads «Кейс №N. …» (Spec 025). */}
            {message.parent_type === "case" ? "" : `${message.parent_type === "idea" ? "Идея" : message.parent_type === "note" ? "Материал" : "Задача"}: `}
            {message.context_title}
          </Link>
        )}
        {lines.map((line, i) => (
          <NoticeLine key={i} line={line} />
        ))}
        <div className="flex flex-wrap gap-x-4 text-meta">
          {message.parent_type === "task" && (
            <Link to={`/tasks/${message.parent_id}/history?field=${field}`} className="inline-flex min-h-touch items-center text-p-link">
              История правок
            </Link>
          )}
          <button type="button" className="min-h-touch text-p-link" onClick={onReply}>
            Ответить
          </button>
        </div>
      </div>
    </article>
  );
}

/** «Изменено 25.09 14:20 · Агент Александра · История (3)» under the description. */
export function DescriptionChanged({ taskId }: { taskId: string }) {
  const history = useQuery({
    queryKey: ["task-history", taskId, "description"],
    queryFn: () => Tasks.history(taskId, { field: "description" }),
  });
  const revisions = history.data?.revisions ?? [];
  const latest = revisions[0];
  if (!latest || latest.origin !== "edit") return null;
  const at = parseDate(latest.created_at);
  return (
    <Link to={`/tasks/${taskId}/history`} className="block text-meta text-hint">
      Изменено {at ? formatDateTime(at) : ""} · {latest.author_name ?? "участник"} ·{" "}
      <span className="text-accent">История ({revisions.length})</span>
    </Link>
  );
}
